import type { AriaSort } from '../../lib/dataTable/useTableSort.js';

export interface SortableColumnHeaderProps {
  /** Visible column label — also the button's accessible name; no separate "sort" copy is folded in (see the header button's CSS: the arrow is `aria-hidden`, and `aria-sort` on the `<th>` is what a screen reader announces). */
  label: string;
  ariaSort: AriaSort;
  onSort(): void;
  /** Right-aligns the cell, matching every numeric/money column elsewhere in the admin. */
  align?: 'right';
}

const INDICATOR: Record<AriaSort, string> = {
  ascending: '▲',
  descending: '▼',
  none: '↕',
};

/**
 * Drop-in replacement for a plain `<th>{label}</th>` that adds sorting: a
 * real `<button>` (not a `<th onClick>`, which is not keyboard- or
 * screen-reader-operable on its own) plus `aria-sort` on the cell itself,
 * the WAI-ARIA pattern for a sortable column header. The direction indicator
 * is a decorative glyph — dimmed until the column is the active sort, so an
 * unsorted table doesn't read as "everything is sorted, ascending".
 */
export function SortableColumnHeader({ label, ariaSort, onSort, align }: SortableColumnHeaderProps) {
  return (
    <th className={align === 'right' ? 'numeric' : undefined} aria-sort={ariaSort}>
      <button type="button" className="th-sort" onClick={onSort}>
        {label}
        <span className={`th-sort__indicator${ariaSort === 'none' ? ' th-sort__indicator--idle' : ''}`} aria-hidden="true">
          {INDICATOR[ariaSort]}
        </span>
      </button>
    </th>
  );
}
